import { ref, ComputedRef, inject } from 'vue';
import { JsonFormsSubStates } from '@jsonforms/core';
import { resourceMakeApiCall, subValWorkflow } from '@/pages/registrations/api/api';
import { useErrors } from './useErrors';
import { interpolateData } from './interpolateData';
import { CommonJsonFormsControl, SchemaWithResource } from './types';

export interface EnumOption {
  label: string;
  value: string;
}

/**
 * Recursively extracts a plain `{ key: value }` map from `data` shaped like `schema`,
 * used to build the `inputVariables` payload sent to `subValWorkflow`. Nested object
 * properties are flattened into nested objects (mirroring the schema), everything else
 * is copied over as-is (including `undefined` for missing values).
 */
function extractInputVariables(schema: any, data: Record<string, unknown>): Record<string, unknown> {
  // No properties to walk (e.g. schema missing/malformed) — nothing to extract.
  if (!schema?.properties) return {};

  const safeData = data ?? {};
  const result: Record<string, unknown> = {};
  for (const [key, propSchema] of Object.entries<any>(schema.properties)) {
    if ((propSchema as any)?.type === 'object' && propSchema?.properties) {
      // Nested object schema — recurse so the shape of the extracted data mirrors the schema.
      result[key] = extractInputVariables(propSchema, (safeData[key] ?? {}) as Record<string, unknown>);
    } else {
      // Primitive/array/leaf schema — copy the value straight across.
      result[key] = safeData[key];
    }
  }
  return result;
}

/**
 * Resolves an Enum/DestinationIp control's dropdown options either statically (from the
 * control's own `options`/`enum`) or dynamically via a backend `resource` API call, and
 * exposes `subValResource` for per-field async (subVal) validation.
 */
export function useApiCallResource(
  control: ComputedRef<CommonJsonFormsControl>,
  staticOptions?: ComputedRef<EnumOption[]>,
) {
  const apiOptions = ref<EnumOption[]>([]);
  const filteredOptions = ref<EnumOption[]>([]);
  const isApiLoading = ref(false);
  const lastFetchedKey = ref<string | null>(null);

  const jsonforms = inject<JsonFormsSubStates>('jsonforms');

  async function fetchResource(): Promise<void> {
    const schema = control.value?.schema as SchemaWithResource | undefined;
    const resource = schema?.resource;
    // Control has no `resource` schema annotation, or a fetch is already in flight — skip.
    if (!resource || isApiLoading.value) return;

    const rootData = (jsonforms?.core?.data ?? {}) as Record<string, unknown>;

    // Resolve any {{path.to.value}} placeholders in resource.data against the form's root data
    // (e.g. "{{sourceSegment.location}}" -> the currently selected location).
    const data = Object.fromEntries(
      Object.entries(resource?.data ?? {}).map(([key, value]) => {
        return [key, interpolateData(value as string, rootData)];
      }),
    );

    const cacheKey = `${resource.apiCallId}:${JSON.stringify(data)}`;
    // Same resource + same resolved data as last time — options are still fresh, skip refetch.
    if (cacheKey === lastFetchedKey.value) return;

    isApiLoading.value = true;
    apiOptions.value = [];
    lastFetchedKey.value = cacheKey;
    try {
      const rs = await resourceMakeApiCall({
        infraServiceApmId: resource.infraServiceApmId,
        apiCallId: resource.apiCallId,
        data,
      });
      apiOptions.value = rs.getResourceResponse.map((item: string) => ({ label: item, value: item }));
    } catch {
      // silently fail — options remain as previously fetched or empty
    } finally {
      isApiLoading.value = false;
    }
  }

  /** `q-select` `@filter` handler: fetches (if resource-backed) or filters (if static) options. */
  function filterFn(val: string, update: (fn: () => void) => void): void {
    const hasResource = !!(control.value?.schema as SchemaWithResource | undefined)?.resource;
    const applyFilter = (opts: EnumOption[]): EnumOption[] => {
      if (!val) return [...opts];
      const needle = val.toLowerCase();
      return opts.filter((v) => v.label.toLowerCase().includes(needle) || v.value.toLowerCase().includes(needle));
    };

    if (hasResource) {
      // Dynamic options — (re)fetch from the backend, then filter the freshly fetched list.
      fetchResource().then(() => {
        update(() => {
          filteredOptions.value = applyFilter(apiOptions.value);
        });
      });
    } else {
      // Static options — no network call needed, just filter what was passed in.
      update(() => {
        filteredOptions.value = applyFilter(staticOptions?.value ?? []);
      });
    }
  }

  /**
   * Runs backend async validation for a single field annotated with `schema.subVal` (a
   * sub-schema id), reporting any resulting error into the shared `useErrors` state.
   */
  async function subValResource(): Promise<void> {
    const subVal = (control.value?.schema as SchemaWithResource | undefined)?.subVal;
    // Control isn't marked for sub-schema validation — nothing to do.
    if (!subVal) return;

    // Field is empty — nothing to validate yet.
    if (Array.isArray(control.value?.data) ? control.value?.data.length === 0 : control.value?.data === '') return;

    const { workflowId } = control.value?.config ?? {};
    const { addError, removeErrorsBySource } = useErrors();
    const rootData = (jsonforms?.core?.data ?? {}) as Record<string, unknown>;
    const rootSchema = jsonforms?.core?.schema;
    const inputVariables = extractInputVariables(rootSchema, rootData);

    const field = control.value.path ?? 'useApiCallOptions';

    const subValData = {
      workflowId,
      inputVariables,
      subSchemaId: subVal,
    };

    // Clear any previous error for this field before re-validating.
    removeErrorsBySource(field);

    try {
      await subValWorkflow(subValData);
    } catch (error) {
      // Validation failed — surface the backend-provided details, or fall back to a generic message.
      const err = error as { response?: { data?: { errorDetails?: string[] } }; message?: string };
      const details: string[] = err?.response?.data?.errorDetails ?? [];
      addError({
        message: details.length ? details.join(', ') : [err?.message ?? 'Validation failed'].join(', '),
        source: field,
      });
    }
  }

  return {
    apiOptions,
    filteredOptions,
    isApiLoading,
    fetchResource,
    filterFn,
    subValResource,
  };
}
